import React, { useEffect, useState, createContext, useContext } from 'react';
interface CurrencyData {
  code: string;
  symbol: string;
  rate: number;
  loaded: boolean;
}
interface CurrencyContextType {
  formatPrice: (usdAmount: number) => string;
  formatOriginalPrice: (usdAmount: number) => string;
  currencyCode: string;
  loaded: boolean;
}
const CurrencyContext = createContext<CurrencyContextType>({
  formatPrice: usd => `$${usd.toFixed(2)}`,
  formatOriginalPrice: usd => `$${usd.toFixed(2)}`,
  currencyCode: 'USD',
  loaded: false
});
// Common country → currency mapping
const COUNTRY_CURRENCY: Record<string, string> = {
  US: 'USD',
  CA: 'CAD',
  GB: 'GBP',
  AU: 'AUD',
  NZ: 'NZD',
  MX: 'MXN',
  CO: 'COP',
  AR: 'ARS',
  CL: 'CLP',
  PE: 'PEN',
  BR: 'BRL',
  UY: 'UYU',
  EC: 'USD',
  VE: 'USD',
  CR: 'CRC',
  PA: 'USD',
  GT: 'GTQ',
  HN: 'HNL',
  SV: 'USD',
  NI: 'NIO',
  DO: 'DOP',
  PR: 'USD',
  CU: 'CUP',
  BO: 'BOB',
  PY: 'PYG',
  ES: 'EUR',
  FR: 'EUR',
  DE: 'EUR',
  IT: 'EUR',
  PT: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  AT: 'EUR',
  IE: 'EUR',
  FI: 'EUR',
  GR: 'EUR',
  JP: 'JPY',
  KR: 'KRW',
  CN: 'CNY',
  IN: 'INR',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK',
  CH: 'CHF',
  PL: 'PLN',
  CZ: 'CZK',
  HU: 'HUF',
  RO: 'RON',
  BG: 'BGN',
  HR: 'EUR',
  ZA: 'ZAR',
  IL: 'ILS',
  TR: 'TRY',
  RU: 'RUB',
  UA: 'UAH',
  PH: 'PHP',
  TH: 'THB',
  MY: 'MYR',
  SG: 'SGD',
  ID: 'IDR',
  VN: 'VND',
  TW: 'TWD',
  HK: 'HKD',
  AE: 'AED',
  SA: 'SAR',
  EG: 'EGP',
  NG: 'NGN',
  KE: 'KES',
  GH: 'GHS'
};
// Currency symbols for display
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  CAD: 'CA$',
  GBP: '£',
  AUD: 'A$',
  NZD: 'NZ$',
  EUR: '€',
  MXN: 'MX$',
  COP: 'COL$',
  ARS: 'AR$',
  CLP: 'CL$',
  PEN: 'S/',
  BRL: 'R$',
  JPY: '¥',
  KRW: '₩',
  CNY: '¥',
  INR: '₹',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  CHF: 'CHF',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  ZAR: 'R',
  ILS: '₪',
  TRY: '₺',
  RUB: '₽',
  PHP: '₱',
  THB: '฿',
  MYR: 'RM',
  SGD: 'S$',
  IDR: 'Rp',
  TWD: 'NT$',
  HKD: 'HK$',
  AED: 'AED',
  SAR: 'SAR',
  DOP: 'RD$',
  CRC: '₡',
  GTQ: 'Q',
  PYG: '₲',
  BOB: 'Bs',
  UYU: '$U',
  NGN: '₦'
};
export function CurrencyProvider({
  children
}: {
  children: ReactNode;
}) {
  const [currency, setCurrency] = useState<CurrencyData>(() => {
    // Check localStorage for cached currency
    try {
      const cached = localStorage.getItem('detected_currency');
      if (cached) {
        const parsed = JSON.parse(cached);
        // Cache for 24 hours
        if (parsed.timestamp && Date.now() - parsed.timestamp < 86400000) {
          return {
            code: parsed.code,
            symbol: parsed.symbol,
            rate: parsed.rate,
            loaded: true
          };
        }
      }
    } catch {}
    return {
      code: 'USD',
      symbol: '$',
      rate: 1,
      loaded: false
    };
  });
  useEffect(() => {
    // Skip if already loaded from cache
    if (currency.loaded) return;
    let cancelled = false;
    async function detectCountry(): Promise<string> {
      // Try primary API
      try {
        const res = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(4000)
        });
        if (res.ok) {
          const data = await res.json();
          if (data.country_code) return data.country_code;
        }
      } catch {}
      // Fallback API 1
      try {
        const res = await fetch('http://ip-api.com/json/?fields=countryCode', {
          signal: AbortSignal.timeout(4000)
        });
        if (res.ok) {
          const data = await res.json();
          if (data.countryCode) return data.countryCode;
        }
      } catch {}
      // Fallback: use browser timezone to guess country
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const tzCountryMap: Record<string, string> = {
          'America/Toronto': 'CA',
          'America/Vancouver': 'CA',
          'America/Edmonton': 'CA',
          'America/Winnipeg': 'CA',
          'America/Halifax': 'CA',
          'America/St_Johns': 'CA',
          'America/Regina': 'CA',
          'America/Montreal': 'CA',
          'Europe/London': 'GB',
          'Europe/Madrid': 'ES',
          'Europe/Paris': 'FR',
          'Europe/Berlin': 'DE',
          'Europe/Rome': 'IT',
          'Europe/Amsterdam': 'NL',
          'Europe/Lisbon': 'PT',
          'Europe/Dublin': 'IE',
          'Australia/Sydney': 'AU',
          'Australia/Melbourne': 'AU',
          'Australia/Perth': 'AU',
          'Pacific/Auckland': 'NZ',
          'America/Mexico_City': 'MX',
          'America/Bogota': 'CO',
          'America/Lima': 'PE',
          'America/Santiago': 'CL',
          'America/Buenos_Aires': 'AR',
          'America/Sao_Paulo': 'BR',
          'Asia/Tokyo': 'JP',
          'Asia/Seoul': 'KR',
          'Asia/Shanghai': 'CN',
          'Asia/Kolkata': 'IN',
          'Asia/Calcutta': 'IN'
        };
        if (tzCountryMap[tz]) return tzCountryMap[tz];
      } catch {}
      return 'US';
    }
    async function detectCurrency() {
      try {
        const countryCode = await detectCountry();
        const currencyCode = COUNTRY_CURRENCY[countryCode] || 'USD';
        // If already USD, no conversion needed
        if (currencyCode === 'USD') {
          if (!cancelled) {
            const result = {
              code: 'USD',
              symbol: '$',
              rate: 1,
              loaded: true
            };
            setCurrency(result);
            localStorage.setItem('detected_currency', JSON.stringify({
              ...result,
              timestamp: Date.now()
            }));
          }
          return;
        }
        // Get exchange rate
        const rateRes = await fetch(`https://api.exchangerate-api.com/v4/latest/USD`, {
          signal: AbortSignal.timeout(5000)
        });
        if (!rateRes.ok) throw new Error('Rate failed');
        const rateData = await rateRes.json();
        const rate = rateData.rates?.[currencyCode];
        if (!cancelled && rate) {
          const result = {
            code: currencyCode,
            symbol: CURRENCY_SYMBOLS[currencyCode] || currencyCode + ' ',
            rate,
            loaded: true
          };
          setCurrency(result);
          localStorage.setItem('detected_currency', JSON.stringify({
            ...result,
            timestamp: Date.now()
          }));
        } else if (!cancelled) {
          setCurrency({
            code: 'USD',
            symbol: '$',
            rate: 1,
            loaded: true
          });
        }
      } catch {
        if (!cancelled) setCurrency({
          code: 'USD',
          symbol: '$',
          rate: 1,
          loaded: true
        });
      }
    }
    detectCurrency();
    return () => {
      cancelled = true;
    };
  }, [currency.loaded]);
  const formatPrice = (usdAmount: number): string => {
    const converted = usdAmount * currency.rate;
    // For currencies with large values (JPY, KRW, CLP, COP, etc.), show no decimals
    const noDecimalCurrencies = ['JPY', 'KRW', 'CLP', 'COP', 'VND', 'IDR', 'HUF', 'PYG'];
    const decimals = noDecimalCurrencies.includes(currency.code) ? 0 : 2;
    const formatted = converted.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    return `${currency.symbol}${formatted}`;
  };
  const formatOriginalPrice = (usdAmount: number): string => {
    return formatPrice(usdAmount);
  };
  return <CurrencyContext.Provider value={{
    formatPrice,
    formatOriginalPrice,
    currencyCode: currency.code,
    loaded: currency.loaded
  }} data-id="element-842">
      {children}
    </CurrencyContext.Provider>;
}
export function useCurrency() {
  return useContext(CurrencyContext);
}